import { type Key, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { type SelectItem } from '@pkgs/components/components/Select';
import { Select } from '@pkgs/components';
import { Language, type Listing } from '@pkgs/api';

import { mapLanguageCode } from '@/utils/settings';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useExposeLanguageStore } from '@/hooks/useExposeLanguageStore';
import { GlobeIcon } from '@/components/common/Icons/GlobeIcon';

import styles from './ExposeLanguageSelector.module.css';

export const ExposeLanguageSelector = ({ listing }: { listing: Listing }) => {
  const { t: tCommon } = useTranslation(TRANSLATION_NAMESPACE.common);
  const { t: tPropertyExpose } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { trackExposeNavigationEvent } = useExposeTracking();

  const { pageLanguages } = useExposePageProps();
  const [hasChangedSelection, setHasChangedSelection] = useState(false);
  const exposeLanguage = useExposeLanguageStore((state) => state.exposeLanguage ?? listing.profile.language);
  const setExposeLanguage = useExposeLanguageStore((state) => state.setExposeLanguage);

  // Hide language selector if there is only one language or no languages
  if (!listing.languages || listing.languages.length <= 1) return null;

  // Hide language selector if all listing languages are already in pageLanguages
  if (!listing.languages.some((lang) => !pageLanguages?.includes(lang))) return null;

  const supportedLanguages: SelectItem[] = listing.languages
    .map((language) => {
      const { value, translationKey } = mapLanguageCode[language];
      return {
        id: value,
        label: `${tCommon(translationKey)} (${value.toUpperCase()})`,
      };
    })
    // sort selected language to front and sort the rest alphanumerically
    .toSorted((a, b) => {
      if (a.id === exposeLanguage) return -1;
      if (b.id === exposeLanguage) return 1;
      return a.label.localeCompare(b.label, 'en', { numeric: true });
    });

  const labelText = tPropertyExpose('search.expose.availableLanguages', {
    count: listing.languages.length,
  });
  const selectedLanguage = hasChangedSelection && listing.languages.includes(exposeLanguage) ? exposeLanguage : undefined;

  const handleLanguageChange = (newLanguage: Key | null) => {
    if (!newLanguage || typeof newLanguage === 'number' || typeof newLanguage === 'bigint' || !Object.keys(Language).includes(newLanguage))
      return;

    setExposeLanguage(newLanguage as Language);
    setHasChangedSelection(true);
    trackExposeNavigationEvent({ elementId: 'switch-language-description_button', selectedOption: newLanguage });
  };
  return (
    <div className={styles.container}>
      <Select
        usesButtonWidth
        buttonIcon={<GlobeIcon />}
        arrowVariant="small"
        items={supportedLanguages}
        value={selectedLanguage}
        placeholder={labelText}
        onChange={handleLanguageChange}
        variant="basicLight"
        hasLabelHidden={true}
        label={tPropertyExpose('search.expose.availableLanguages.label')}
        accessibilityLabel={tPropertyExpose('search.select.hiddenAccessibilityText', {
          label: tPropertyExpose('search.expose.availableLanguages.label'),
        })}
      />
    </div>
  );
};
